import React from "react";
import DtvTheme, { colorPalette } from "@directv-digital/dds.ui.dtv-theme";

const bps = DtvTheme.breakpoints;

import CardMedia from "@mui/material/CardMedia";
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import Button from "@directv-digital/fusion.atoms.button";
import DtvText from "../../../video-lab/dtv-text";
import Divider from "@directv-digital/fusion.molecules.divider";
import Typography from "@mui/material/Typography";
import ZipcodeModule from "src/components/video-lab/zipcode-module";
import { IPackage } from "@/ChnlListingPageV2/chnlListingPage.props";
import { zIndexMap } from "@/ChnlListingPageV2/utils/zIndexMap";
import { tooltipStyles } from "../IconBanner.styles";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";
import ChannelInfoIcon from "@/shared/ChannelInfoIcon";
import { channelLogoSx, channelLogoChipFor, handleChannelLogoError } from "lib/helper/channel-logo-url";

const MockIconBg = () => (
  <svg viewBox="0 0 261 261" xmlns="http://www.w3.org/2000/svg" style={{
    position: 'absolute',
top: 0,
left: 0,
right: 0,
bottom: 0,
    paddingTop: "2px",
    zIndex: zIndexMap.channelIconBackground,
    transform: "scale(0.9)"
  }}>
    <g><circle cx="108" cy="108" data-name="Ellipse 34" fill="#747474" r="108" transform="translate(22.5 20.5)"/></g>
  </svg>
);

const MYHOMETEAMCTA_ENDICON_SRC = "https://www.directv.com/dtvassets/global/icons/2022/arrow-right_dk-bg.svg";

const LocalRsnTooltip = ({ type, tooltipProps, genreNationalLabel = '' }) => {
  // The badge label is intrinsic to the channel's classification (Local/RSN/National),
  // not to the CMS hover copy — so it must render whenever a local/RSN/national channel
  // is shown, even if the tooltip content is empty. Gating the whole badge on
  // `tooltipProps?.content` made the badge silently vanish whenever the
  // localAndRegionalChannels.localTooltip / rsnTooltip CMS field was empty (the
  // flag-ON channels-service story divergence from prod). The hover Tooltip wrapper is
  // applied only when there is content; otherwise the bare label renders.
  const hasTooltipContent = Boolean(tooltipProps?.content);
  const badge = (
    <Typography variant="body2" sx={{
      minWidth: type === "national" ? "66px" : "53px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "2px",
      whiteSpace: "nowrap",
borderRadius: "4px",
      color: "#ffff",
backgroundColor: "rgb(85, 85, 85)",
      padding: "2px",
margin: "0px auto",
      fontSize: "12px !important",
letterSpacing: "-0.02em",
      cursor: hasTooltipContent ? 'pointer' : 'default'
    }}>
      {type === 'national' ? genreNationalLabel :  type === 'local' ? 'Local' : 'RSN'}
      {hasTooltipContent && <ChannelInfoIcon />}
    </Typography>
  );

  return (
    <DtvBox component="span" sx={{
      textAlign: 'center',
      width: 'auto',
      position: 'absolute',
      left: '50%',
bottom: "0px",
transform: 'translateX(-50%)',
      zIndex: zIndexMap.ctgBannerLocalRsnTooltipCtnr
    }}>
      {hasTooltipContent ? (
        <Tooltip
          describeChild
          content={tooltipProps.content}
          enterTouchDelay={0}
          leaveTouchDelay={3000}
          placement="bottom"
          PopperProps={{
            sx: {
              "& .MuiTypography-root": {
                fontFamily: `roboto-regular`,
                fontSize: "14px"
              }
            }
          }}
          sx={{
            marginTop: "-20px"
          }}
          tooltipStyles={{
            backgroundColor: `${colorPalette.neutral_white.main} !important`,
            color: `${colorPalette.jet.main} !important`
          }}
          >
            {badge}
        </Tooltip>
      ) : badge}
    </DtvBox>
  );
};

const ShowMoreButton = ({ showMoreKey, ctgIndex, ctgKey, handleShowMoreClick, mode, textColor }) => (
  <DtvBox
    id={`chnlCtg-show-more-${ctgIndex}`}
    sx={{
      display: "block",
      visibility: "hidden", // initially hide from DOM
      pointerEvents: {
        xs: "auto",
        sm: "none"
      },
      textAlign: "center",
      margin: "12px 0 12px",
      position: "relative"
    }}>
    <DtvButton
      ariaLabel={!showMoreKey ? 'Show more channels' : 'Show Less channels'}
      label={!showMoreKey ? 'Show More' : 'Show Less'}
      variant= "uxtext"
      endIcon={
        <CardMedia
          alt={!showMoreKey ? "Show More channels icon" : "Show Less channels icon"}
          component="img"
          loading="lazy"
          src={"https://www.directv.com/dtvassets/global/icons/2022/arrow_down_gray700.svg"}
          sx={{
            height: "16px",
            width: "16px",
            filter: mode === "dark" ?
              "brightness(0) saturate(100%) invert(88%) sepia(100%) saturate(26%) hue-rotate(65deg) brightness(111%) contrast(106%)" :
              "brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(6600%) hue-rotate(180deg) brightness(93%) contrast(95%)"
              // Tanner: svg coloring - https://stackoverflow.com/questions/22252472/how-can-i-change-the-color-of-an-svg-element
          }}
        />
      }
      sx={{
        "& img": {
          transform: !showMoreKey ? "rotate(0deg)" : "rotate(180deg)",
          transition: "transform 200ms linear"
        },
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "18.2px",
        width: "120px",
        height: "36px",
        textDecoration: "none !important",
        color: textColor,
        transition: "all ease 200ms",
        "&:focus": {
          outline: "4px solid #1FBCF8",
          color: textColor
        },
        "&:hover": {
          backgroundColor: "#FFF", // #3885E1
          color: mode === "dark" ? colorPalette.jet.main : textColor,
          "& img": {
            filter: mode === "dark" ?
              "brightness(0) saturate(100%) invert(10%) sepia(68%) saturate(1229%) hue-rotate(186deg) brightness(95%) contrast(93%) !important" :
              "brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(6600%) hue-rotate(180deg) brightness(93%) contrast(95%)"
          }
        },
        "&:active": {
          backgroundColor: "#1FBCF83D",
          color: textColor,
          "& img": {
            filter: mode === "dark" ?
              "brightness(0) saturate(100%) invert(88%) sepia(100%) saturate(26%) hue-rotate(65deg) brightness(111%) contrast(106%)" :
              "brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(6600%) hue-rotate(180deg) brightness(93%) contrast(95%)"
          }
        }
      }}
      onClick={evt => {
        evt?.target?.blur();
        handleShowMoreClick(ctgKey);
      }}
    />
  </DtvBox>
);

// WAT-11225 (AC4): genre pack "Shop" CTA renders as a configurable button
// (label sourced from CI via gpCtaProps). Suppressed unless BOTH a label and a target URL are
// authored — `ctaUrl` defaults to "", and an anchor with an empty href silently reloads the
// current page, so a label-only CMS entry would otherwise render a dead "Shop" button.
const CategoryHeaderCta = ({ mode, ctaLabel, ctaUrl, sx }) => (
  ctaLabel && ctaUrl ? (
    <Button
      href={ctaUrl}
      aria-label={ctaLabel}
      mode={mode || "light"}
      size={"large"}
      buttontype={"primary"}
      isTangerine={true}
      role="button"
      tabIndex={0}
      googleanalyticsdata={getAnalyticsAttrs(ctaLabel?.toLowerCase(), "body", ctaUrl)}
      sx={{
        minWidth: "fit-content",
        height: "36px !important",
        [bps.up(1200)]: {
          height: "56px !important"
        },
        ...sx
      }}
    >
      {ctaLabel}
    </Button>
  ) : null
);

const CategoryFooterCta = ({ mode, footerCta }) => (
  <DtvButton
    ariaLabel={footerCta?.label}
    googleAnalyticsData={getAnalyticsAttrs(footerCta?.label?.toLowerCase(), "body", footerCta?.targeturl)}
    label={footerCta?.label}
    modalData={{ url: footerCta?.targeturl }}
    modalType={"iframe"}
    mode={mode === "dark" ? "light" : "dark"}
    variant={"uxtext"}
    endIcon={
      <img
        alt={footerCta?.label}
        loading="lazy"
        src={MYHOMETEAMCTA_ENDICON_SRC}
        style={{
          height: "16px",
          ...mode === "light" && {
            filter: "brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(6600%) hue-rotate(180deg) brightness(93%) contrast(95%)"
          }
        }}
      />
    }
    sx={{
      mt: { xs: "10px" },
      ml: {
 xs: "16px",
sm: "0px"
},
      marginBottom: {
 xs: "5.85px",
sm: "0px"
},
      paddingTop: {
 xs: "5px",
sm: "0px"
},
      paddingBottom: {
 xs: "7px",
sm: "5px"
},
      textDecoration: "none !important",
      alignSelf: { xs: 'flex-start'  },
      fontSize: {
 sm: "14px !important",
md: "16px !important"
},
      '& .MuiButton-endIcon, .DtvButton-endIcon': {
        display: 'flex',
        alignSelf: 'end'
      },
      "&:hover,&:focus,&:active": {
        backgroundColor: "unset",
        textDecoration: "underline !important"
      }
    }}
  />
);

const IconCategory = ({
  channels,
  ctgKey,
  ctgValue,
  ctgIndex,
  isLast,
  packageData,
  showMoreKey,
  handleShowMoreClick,
  footerCta,
  zipCodeModuleProps,
  localChnlBadgeTooltip,
  rsnChnlBadgeTooltip,
  theming,
  genreNationals,
  subscriptionsLabel,
  channelsLabel
}) => {
  const iconUrl = (packageData as IPackage)?.icon || "";
  // WAT-11225: included SVODs for this genre pack (Subscriptions row)
  const svods = (packageData as IPackage)?.svods || [];
  const ctaUrl = (packageData as IPackage)?.gpCtaProps?.targeturl || "";
  const ctaLabel = (packageData as IPackage)?.gpCtaProps?.label || "";
  const ctgHeaderTooltip =
    (ctgKey === "RSN" && rsnChnlBadgeTooltip) ? rsnChnlBadgeTooltip :
      (ctgKey === "LOCAL" && localChnlBadgeTooltip) ? localChnlBadgeTooltip :
        (packageData?.tooltip?.content) ? packageData?.tooltip : {};
  try {
    return (
    <DtvBox id={`ChnlListingPage-iconCtg-${ctgIndex}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        maxWidth: "100%",
        minWidth: "100%",
        px: {
          xs: "14px",
          sm: "90px",
          md: "196px"
        },
        "& .MuiDivider-root": { width: "100% !important" }
    }}>
      {/** Category Header */}
      <DtvBox
        sx={{
          display: 'flex',
          justifyContent: "space-between",
          // flexing to columns at 768 since SVOD names need to wrap sooner than mobile
          [bps.down(768)]: {
            flexDirection: 'column'
          },
          alignItems: 'flex-start',
          flexWrap: 'nowrap',
          position: "relative",
          paddingBottom: "8px"
        }}
      >
        <DtvBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            // WAT-11225: 4px left inset so the genre pack name aligns with the
            // Subscriptions row + channel logos.
            pl: "4px",
            [bps.up(768)]: {
            maxWidth: "70%"
            },
            minHeight: {
 xs: "24px",
md: "28px"
}
          }}>
            {iconUrl && <>
              <DtvBox sx={{
 display: "flex",
height: "100%",
alignItems: "flex-start"
}}>
              <img
                // [WAT-12043 / YFH-AMP111 audit annotation] For the MYFREE_DTV
                // row this <img> IS the MyFree DIRECTV brand logo (110x31),
                // not a generic pack glyph — the audit calls for that specific
                // brand text as its accessible name. Other packs' icons stay
                // on the generic label until they get per-pack alts.
                alt={ctgKey === "MYFREE_DTV" ? "MyFree DIRECTV" : "Genre Pack icon"} src={iconUrl}
                style={{
                  width: "28px",
height: "28px",
marginRight: "4px",
                  ...theming?.mode === "light" && { filter: "brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(6600%) hue-rotate(180deg) brightness(93%) contrast(95%)" },
                  ...(ctgKey === "MYFREE_DTV" && {
 width: "110px",
height: "31px"
})
                }}
              />
              </DtvBox>
            </>}
            <DtvText
              // [WAT-12043 / YFH-AMP111] Renders each per-category section
              // heading rendered by IconBanner (Genre-Pack tab titles,
              // MyFree DIRECTV section, and any other CMS-driven category
              // this component is mounted for). All of them are visible
              // section headings under the page <h1>, so render as <h2>.
              // Kept generic because the category set is CMS-driven —
              // see `channel-lineup-page-adapter` for the source.
              component="h2"
              variant="subhead1medium"
              content={
                ctgValue?.toUpperCase() === "SPORTS" ? "NATIONAL SPORTS" :
                packageData?.isGenreMiniPack === true ? `${ctgValue} | Mini Pack` :
                ctgValue
              }
              sx={{
                color: theming?.textColor,
                ...(ctgKey === "MYFREE_DTV" && { marginLeft: "25px" }),
                "& span": {
                  marginLeft: "12px",
                  // The Fusion tooltip renders the icon low relative to the large title; nudge it
                  // up 3px to sit level with the text.
                  position: "relative",
                  bottom: "3px",
                  color: `${theming?.textColor} !important`
                }
              }}
              tooltip={{
                closeOnScroll: true,
                ...ctgHeaderTooltip
              }}
            />
        </DtvBox>
        {/* WAT-11225: desktop keeps the CTA in the header (top-right); on mobile it
            renders after the channels grid (see below), per Figma. */}
        <CategoryHeaderCta
          ctaLabel={ctaLabel}
          ctaUrl={ctaUrl}
          mode={theming?.mode}
          sx={{
            display: "none",
            [bps.up(768)]: {
              display: "inline-flex",
              alignSelf: "flex-start",
              margin: "0px 0px 0px 26px"
            }
          }}
        />
      </DtvBox>

      {ctgKey === "MYHOMETEAM" && (
        <DtvBox sx={{
          marginLeft: "32px",
          // ensure the "Hide/Change" doesn't wrap at ~600-650px
          [`& #${zipCodeModuleProps.id}-header-ctnr`]: {
            marginRight: {
 sm: "-60px !important",
md: 0
}
          }
        }}>
          <ZipcodeModule { ...zipCodeModuleProps }/>
        </DtvBox>
      )}

      {/** Subscriptions (included SVODs) — WAT-11225 */}
      {svods?.length > 0 && (
        <DtvBox
          id={`ChnlListingPage-svods-${ctgIndex}`}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: {
              xs: "8px",
              sm: "0px",
              md: "0px"
            },
            paddingBottom: {
              xs: "13px",
              sm: "12px",
              md: "20px"
            },
            // WAT-11225: 4px left inset so the Subscriptions label + SVOD icons align
            // with the channel logos (which sit ~4px in within their circles).
            pl: "4px"
          }}
        >
          {subscriptionsLabel && (
            <DtvText
              content={subscriptionsLabel}
              variant="body1medium"
              sx={{
                color: theming?.textColor
              }}
            />
          )}
          <DtvBox
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              paddingTop: {
                xs: "6px",
                sm: "12px"
              },
              paddingBottom: {
                xs: "6px",
                sm: "12px"
              },
              gap: {
                xs: "16px",
                md: "24px"
              }
            }}
          >
            {svods.map((svod, sIx) => {
              const svodIcon = svod?.icon?.default || svod?.icon?.tablet || svod?.icon?.mobile;
              const svodIconImg = (
                <CardMedia
                  component="img"
                  src={svodIcon}
                  alt={svod?.name || "Included subscription"}
                  loading="lazy"
                  sx={{
                    height: {
                      xs: "44px",
                      sm: "56px",
                      md: "80px"
                    },
                    width: "auto",
                    objectFit: "contain"
                  }}
                />
              );
              return svodIcon ? (
                svod?.name ? (
                  // WAT-11225: styled Fusion tooltip (matches the channel logos), not the
                  // native title box.
                  <Tooltip
                    key={`svod-${ctgKey}-${sIx}`}
                    describeChild
                    content={svod?.name}
                    disableCursorPointer={true}
                    enterTouchDelay={0}
                    placement="bottom"
                    PopperProps={{
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          enabled: true,
                          phase: 'main'
                        }, {
                          name: 'flip',
                          enabled: true,
                          phase: 'main'
                        }, {
                          name: 'offset',
                          options: {
                            offset: [0, -8]
                          }
                        }],
                      sx: {
                        width: 'unset !important',
                        "& .MuiTypography-root": {
                          fontFamily: `roboto-regular`,
                          fontSize: "14px"
                        }
                      }
                    }}
                    sx={{
                      "& #popper[data-popper-escaped]": {
                        visibility: 'hidden'
                      }
                    }}
                    tooltipStyles={{
                      ...tooltipStyles.tooltip
                    }}
                  >
                    {svodIconImg}
                  </Tooltip>
                ) : (
                  <React.Fragment key={`svod-${ctgKey}-${sIx}`}>{svodIconImg}</React.Fragment>
                )
              ) : null;
            })}
          </DtvBox>
        </DtvBox>
      )}

      {channelsLabel && channels?.length > 0 && (
        <DtvText
          content={channelsLabel}
          variant="body1medium"
          sx={{
            color: theming?.textColor,
            pl: "4px",
            paddingBottom: {
              xs: "8px",
              sm: "8px",
              md: "12px"
            }
          }}
        />
      )}

      {/** Channel Icons */}
      <DtvBox className={"chnlCtg-icons"} id={ctgKey}
        sx={{
          display: "grid",
          // WAT-11225: on mobile the columns STRETCH to fill the row (minmax(74px, 1fr))
          // instead of being pinned at 74px. Fixed columns left a ragged tail — at 375px
          // only 4 of them fit, so the grid stopped ~42px short of the content edge while
          // the full-width Shop CTA below ran the whole way, making the CTA read as
          // right-aligned. Figma spreads the icons edge to edge, which also squares the
          // CTA up with the last column. md keeps its fixed 104px track.
          gridTemplateColumns: {
            xs: "repeat(auto-fit, minmax(74px, 1fr))",
            sm: "repeat(auto-fit, minmax(72px, 1fr))",
            md: "repeat(auto-fill, 104px)"
          },
          // Keep each logo centred in its (now wider) track.
          justifyItems: {
            xs: "center",
            sm: "center",
            md: "stretch"
          },
          gap: {
            xs: "4.5px 3px",
            sm: "4.5px 3px",
            md: "6px 4px"
          },
          // WAT-11225: left-align channels on all breakpoints so the grid lines up
          // with the left-aligned Subscriptions row + pack title (Figma). Previously
          // centered on xs/sm, which left the first column indented from the content edge.
          justifyContent: "flex-start",
          overflow: "hidden",
          width: '100%',
          maxHeight: {
            xs: showMoreKey ? "5000px" : "475px",
            sm: "5000px"
          },
          transition: "max-height 300ms ease-in-out 100ms"
      }}>
        {channels.map((channel, index) => {
          const normalizedCtgKey = ctgKey?.toLowerCase();
          // WAT-11362: defined once so the OV branch can wrap it in the chip while the
          // flag-off branch renders it bare — keeping img.parentElement, and therefore the
          // hide-on-error target, exactly as production has it.
          const channelLogoImg = (
                      // [WAT-12043 / YFH-AMP112] onClick previously called a
                      // no-op stub (IconBanner_wAddOnChnls handleIconClick =
                      // console.log). aria-labelledby pointed at the wrapper
                      // div which itself carried a misleading "Click the X
                      // icon to view available packages" aria-label. Both are
                      // gone — the CardMedia's `alt={channel?.name}` now
                      // supplies the accessible name cleanly, and the icon is
                      // presentational per WCAG 2.1.1 / 4.1.2.
                      <CardMedia
                      alt={channel?.name}
                      component="img"
                      key={channel?.name}
                      loading={(ctgIndex === 1 || ctgIndex === 2) ? "eager" : "lazy"}
                      src={channel.icon}
                      sx={{
                        paddingTop: "1.3px", // eyeballed to center svg exactly
                        ...channelLogoSx(channel?.isOvLogo, channel?.icon),
                        fontSize: "8px",
color: "#FFF",
                        fill: "#747474",
                        // contentVisibility: (ctgIndex !== 1 && ctgIndex !== 2) ? "auto" : "visible",
                        position: "relative",
                        zIndex: zIndexMap.channelIcon
                      }}
                      // WAT-11362: an OV imageURL can 404 (verified on News Mix / Heroes &
                      // Icons). Hiding the element left an empty chip; swap to the no-logo
                      // asset so the channel still reads as present.
                      onError={e => handleChannelLogoError(e, channel?.isOvLogo, "hideParent")}
                    />
          );
          const isRSNLocalChannel = Boolean(channel?.isLocal || channel?.isRsn) || ['rsn', 'local'].includes(channel?.category?.toLowerCase?.())
          const genreNationalData = genreNationals?.genreNationalMapping?.find(item => item.channel === channel?.name);
          const isFirstChannelWithBadge = index === 0 && (
            channel?.isNational?.[normalizedCtgKey] ||
            isRSNLocalChannel
          );
          return (
            // [WAT-12043 / YFH-AMP112] Wrapper is now purely presentational —
            // no tabIndex, no aria-label, no focus-visible outline. The
            // click behaviour was a no-op stub (see handleIconClick removal
            // in IconBanner_wAddOnChnls.tsx) so putting this in the tab
            // order and announcing it as "Click the X icon..." failed WCAG
            // 2.1.1 (Keyboard) / 4.1.2 (Name, Role, Value). Screen reader
            // users still hear each channel via the child CardMedia's alt.
            <DtvBox id={`icon-container-${channel?.category}-${index}`}
              key={`Banner-${channel?.uuid}`}
              sx={{
                backgroundColor: "transparent",
                borderRadius: "50%",
                position: "relative",
                width: {
  xs: "74px !important",
sm: "72px !important",
md: "104px !important"
},
                height: {
 xs: "74px !important",
sm: "72px !important",
md: "104px !important"
},
                padding: "0px",
                margin: "0px"
                // (the circular badge is drawn on the logo img itself — see channelLogoSx)
            }}>
              {(channel?.name &&
              <Tooltip
                describeChild
                content={channel?.name}
                disableCursorPointer={true}
                enterTouchDelay={0}
                placement={isRSNLocalChannel ? 'top' : 'bottom'}
                PopperProps={{
                  modifiers: [
                  {
                    name: 'preventOverflow',
                    enabled: true,
                    phase: 'main'
                  }, {
                    name: 'flip',
                    enabled: true,
                    phase: 'main'
                  }, {
                    name: 'offset',
                    options: {
                      offset: [0, -20]
                    }
                  }],
                  sx: {
                    width: 'unset !important',
                    "& .MuiTypography-root": {
                      fontFamily: `roboto-regular`,
                      fontSize: "14px"
                    }
                  }
                }}
                sx={{
                  "& #popper[data-popper-escaped]": {
                    visibility: 'hidden'
                  }
                }}
                tooltipStyles={{
                  ...tooltipStyles.tooltip,
                  top: isFirstChannelWithBadge ? {
 xs: "23px",
sm: "0px"
} : "0px",
                  marginLeft: isFirstChannelWithBadge ? {
 xs: "78px",
sm: "0px"
} : "0px"
                }}>
                  <>
                    {/* WAT-11362: flag ON the logo carries its own white badge, so this grey
                        disc would show through transparent artwork as a second ring. */}
                    {!channel?.isOvLogo && <MockIconBg />}
                    {/* WAT-11362: the chip must wrap ONLY the image — channelLogoChipSx uses
                        transform: scale(), which scales the whole subtree, and on the outer box
                        that also shrank the Local/RSN badges. The wrapper is rendered ONLY when
                        the flag is on: `display: contents` would still leave an element, and the
                        "hideParent" fallback keys off img.parentElement — production hides the
                        whole cell, not just the image. */}
                    {channel?.isOvLogo ? (
                      <DtvBox
                        sx={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                          ...channelLogoChipFor(true, channel?.icon)
                        }}
                      >
                        {channelLogoImg}
                      </DtvBox>
                    ) : channelLogoImg}
                  {(channel?.isLocal || channel?.category?.toLowerCase?.() === 'local') && (
                    <LocalRsnTooltip
                      genreNationalLabel={genreNationalData?.nationalTooltipLabel || 'National'}
                      type={channel?.isNational?.[normalizedCtgKey] ? 'national' : 'local'}
                      tooltipProps={
                        channel?.isNational?.[normalizedCtgKey] ?
                          { content: genreNationalData?.nationaltooltipContent || "" } :
                          localChnlBadgeTooltip
                      }
                    />
                  )}
                    {(channel?.isRsn || channel?.category?.toLowerCase?.() === 'rsn') && (
                      <LocalRsnTooltip
                        tooltipProps={rsnChnlBadgeTooltip}
                        type='rsn'
                      />
                    )}
                  </>
              </Tooltip>
              )}
            </DtvBox>
          );
        })}
      </DtvBox>

      <ShowMoreButton
        ctgIndex={ctgIndex}
        ctgKey={ctgKey}
        handleShowMoreClick={handleShowMoreClick}
        mode={theming?.mode}
        showMoreKey={showMoreKey}
        textColor={theming?.textColor}
      />

      {/* WAT-11225: mobile-only Shop CTA — renders after the channels grid, full width (Figma) */}
      <CategoryHeaderCta
        ctaLabel={ctaLabel}
        ctaUrl={ctaUrl}
        mode={theming?.mode}
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "center",
          margin: "24px 0 0 0 !important",
          [bps.up(768)]: {
            display: "none"
          }
        }}
      />

      {/* MyHomeTeam Modal Cta */}
      {footerCta?.label && (
        <CategoryFooterCta footerCta={footerCta} mode={theming?.mode} />
      )}
      {/** Divider */}
      {!isLast ? (
        <Divider mode={theming?.mode} sx={{
          ...theming?.mode === "light" && { backgroundColor: "#D1D1D1 !important" },
          mt: {
 xs: "36px",
sm: "54px",
md: "60px"
},
          mb: {
 xs: "48px",
sm: "54px",
md: "60px"
}

        }}
        />
      ) : (
        <DtvBox sx={{
 mt: {
 xs: "36px",
sm: "48px",
md: "170px"
}
}} />
      )}
    </DtvBox>
    );
  } catch(error) {
    console.error("Error rendering IconCategory:", error);
    return <></>;
  }
};

export default React.memo(IconCategory);
